import { Assets, Container, Sprite, type Texture } from "pixi.js";
import { isPortrait, type Log, type ViewportStore } from "@slot/presentation";

/** A title's two background stills by URL (decision 69); null where the manifest lacks one: the other shows instead. */
export interface StillUrls {
  portrait: string | null;
  landscape: string | null;
}

/** How much larger than the viewport a still is drawn when the title names no overscan: no hairline seam at its edges. */
export const DEFAULT_STILL_OVERSCAN = 1.002;

/** The still a viewport shows: its orientation's (`isPortrait`), else the other one. */
export function stillFor(urls: StillUrls, viewport: { readonly width: number; readonly height: number }): string | null {
  return isPortrait(viewport) ? (urls.portrait ?? urls.landscape) : (urls.landscape ?? urls.portrait);
}

/**
 * The scene's still under the transparent canvas (decision 69, `draw` "dom"): a div at the bottom of the background root,
 * under whatever the scene mounts there whenever it mounts it, the still for the viewport's orientation cover-fitted
 * across it (`overscan` past its edges), swapped in the viewport's own tick as it turns. The browser fetches a still as it
 * is first shown and paints it: nothing is decoded into Pixi.
 */
export class StillBackgroundElement {
  public readonly element: HTMLDivElement;
  private readonly unsubscribe: () => void;

  public constructor(input: { root: HTMLElement; urls: StillUrls; overscan: number; viewport: ViewportStore }) {
    const element = document.createElement("div");
    element.className = "slot-background-still";
    const style = element.style;
    style.position = "absolute";
    style.top = style.right = style.bottom = style.left = "0";
    style.backgroundPosition = "center";
    style.backgroundSize = "cover";
    style.backgroundRepeat = "no-repeat";
    style.pointerEvents = "none";
    style.transform = `scale(${input.overscan})`;
    input.root.prepend(element);
    this.element = element;
    let shown: string | null = null;
    this.unsubscribe = input.viewport.subscribe((snapshot) => {
      const url = stillFor(input.urls, snapshot.css);
      if (url === shown) return;
      shown = url;
      element.style.backgroundImage = url ? `url("${url}")` : "";
    });
  }

  public dispose(): void {
    this.unsubscribe();
    this.element.remove();
  }
}

/** Loads a still's texture by URL (the page's, as the asset registry resolves it): Pixi's `Assets` (a test passes its own). */
export type StillTextureLoader = (url: string) => Promise<Texture>;

/**
 * The scene's still in the canvas (decision 69, `draw` "canvas"): a sprite at the bottom of the game layer, whose origin
 * is the viewport's centre, the still for the viewport's orientation cover-fitted over the design viewport (`overscan`
 * past its edges). A still is decoded as its orientation is first shown; until it is, the one before stays (refitted).
 * `ready` settles once the first has shown, or failed (logged).
 */
export class StillBackgroundView extends Container {
  public readonly ready: Promise<void>;
  private readonly sprite = new Sprite();
  private readonly textures = new Map<string, Texture>();
  private readonly loading = new Map<string, Promise<void>>();
  private readonly urls: StillUrls;
  private readonly overscan: number;
  private readonly load: StillTextureLoader;
  private readonly log: Log;
  private readonly unsubscribe: () => void;
  /** The still the viewport asks for now. */
  private wanted: string | null = null;
  private design = { width: 0, height: 0 };

  public constructor(input: { urls: StillUrls; overscan: number; viewport: ViewportStore; log: Log; load?: StillTextureLoader }) {
    super({ label: "BackgroundStill" });
    this.urls = input.urls;
    this.overscan = input.overscan;
    // The registry's URLs are the page's (as the DOM reads them); Pixi would resolve a relative one against its own base path.
    this.load = input.load ?? ((url) => Assets.load<Texture>(new URL(url, document.baseURI).href));
    this.log = input.log;
    this.sprite.anchor.set(0.5);
    this.sprite.visible = false;
    this.addChild(this.sprite);
    let first: Promise<void> | null = null;
    this.unsubscribe = input.viewport.subscribe((snapshot) => {
      this.design = { width: snapshot.design.width, height: snapshot.design.height };
      this.wanted = stillFor(this.urls, snapshot.design);
      const requested = this.wanted ? this.request(this.wanted) : Promise.resolve();
      first ??= requested;
      this.fit();
    });
    this.ready = first ?? Promise.resolve();
  }

  public override destroy(options?: Parameters<Container["destroy"]>[0]): void {
    this.unsubscribe();
    super.destroy(options);
  }

  /** The still at `url` decoded (once), shown if the viewport still wants it. */
  private request(url: string): Promise<void> {
    if (this.textures.has(url)) return Promise.resolve();
    let pending = this.loading.get(url);
    if (!pending) {
      pending = this.load(url).then(
        (texture) => {
          this.textures.set(url, texture);
          if (!this.destroyed) this.fit();
        },
        (error: unknown) => {
          this.log(`[StillBackground] "${url}" not loaded: ${error instanceof Error ? error.message : String(error)}`);
        },
      );
      this.loading.set(url, pending);
    }
    return pending;
  }

  /** The wanted still, else the one shown, covering the design viewport. */
  private fit(): void {
    const texture = (this.wanted ? this.textures.get(this.wanted) : undefined) ?? (this.sprite.visible ? this.sprite.texture : undefined);
    if (!texture || texture.width <= 0 || texture.height <= 0) return;
    this.sprite.texture = texture;
    this.sprite.visible = true;
    const scale = Math.max(this.design.width / texture.width, this.design.height / texture.height) * this.overscan;
    this.sprite.scale.set(scale, scale);
  }
}
